import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';

// Use unified date utils
import { moment } from '../utils/dateUtils';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Paper,
  Link,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';

// Custom Components
import { Spinner, BrowserTitle, DateSwitcher } from '../components';

// Utils
import messages from '../static/messages';
import { AuthContext } from '../context/AuthProvider';

// Services
import appointmentsServices from '../services/appointmentsServices';

// Hooks
import usePagination from '../hooks/usePagination';

// Styles
import { table_container_base } from '../static/styles';

const link_styles = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
};

const Timer = () => {
  const { userData } = useContext(AuthContext);
  const [date, setDate] = useState(moment().format());
  const [appointments, setAppointments] = useState();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTechnician = userData?.role === 'technician';
  
  // Check if user has extended permissions (All Proposals or All Installations)
  const hasExtendedPermissions = userData?.permissions?.includes('all_proposals') || 
                                  userData?.permissions?.includes('all_installations');

  const isAdmin = ['superadmin', 'admin', 'dispatcher'].includes(userData?.role);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  const handleResetTime = async (appointmentId, field) => {
    if (!window.confirm(`Reset ${field === 'checkIn' ? 'Check In' : 'Check Out'} time?`)) return;
    try {
      const response = field === 'checkIn'
        ? await appointmentsServices.resetCheckIn(appointmentId)
        : await appointmentsServices.resetCheckOut(appointmentId);
      if (response.status === 200) {
        toast.success(`${field === 'checkIn' ? 'Check In' : 'Check Out'} has been reset`);
        getUserAppointments();
      }
    } catch (error) {
      toast.error('Failed to reset time');
      console.error(error);
    }
  };

  // Async functions
  const getUserAppointments = async () => {
    if (!userData?.id) return;
    
    try {
      // Technicians see only their appointments, others see all
      const userId = isTechnician ? userData.id : null;
      
      const response = await appointmentsServices.getAllAppointments(
        date.split('T')[0] + 'T08:00:00.000Z',
        userId
      );

      if (response.status === 206) setAppointments(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Restrict technicians from viewing future dates (except those with extended permissions)
  useEffect(() => {
    if (isTechnician && !hasExtendedPermissions) {
      const today = moment().tz('America/Los_Angeles').startOf('day');
      const selectedDay = moment(date).tz('America/Los_Angeles').startOf('day');
      
      // If technician tries to view future date, reset to today
      if (selectedDay.isAfter(today, 'day')) {
        setDate(moment().format());
        setAppointments([]);
        return;
      }
    }
  }, [date, isTechnician, hasExtendedPermissions]);

  // Hooks
  useEffect(() => {
    getUserAppointments();
  }, [userData?.id, date, isTechnician]);

  // Check for data loading
  if (!appointments) return <Spinner />;

  // Mobile Card Component
  const MobileAppointmentCard = ({ appointment }) => (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        '&:hover': { boxShadow: 3 },
      }}
      onClick={() => window.location.href = `/appointments/${appointment.id}`}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              {appointment.user?.fullName || 'Unassigned'}
            </Typography>
          </Box>
          {appointment.type?.name && (
            <Chip label={appointment.type.name} size="small" color="primary" variant="outlined" />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
          <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.3 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {appointment.property?.address || 'No address'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #eee' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ color: 'success.main', fontSize: 18 }} />
            <Typography variant="body2">
              In: <strong>{appointment?.checkIn ? moment(appointment?.checkIn).tz('America/Los_Angeles').format('hh:mm A') : '-'}</strong>
            </Typography>
            {isAdmin && appointment?.checkIn && (
              <Tooltip title="Reset Check In">
                <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }} color="error"
                  onClick={(e) => { e.stopPropagation(); handleResetTime(appointment.id, 'checkIn'); }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ color: 'error.main', fontSize: 18 }} />
            <Typography variant="body2">
              Out: <strong>{appointment?.checkOut ? moment(appointment?.checkOut).tz('America/Los_Angeles').format('hh:mm A') : '-'}</strong>
            </Typography>
            {isAdmin && appointment?.checkOut && (
              <Tooltip title="Reset Check Out">
                <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }} color="error"
                  onClick={(e) => { e.stopPropagation(); handleResetTime(appointment.id, 'checkOut'); }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box component="section">
      <BrowserTitle title="Timer | Airmaxx Pro" />

      {/* Date Switcher with maxDate restriction for technicians (except those with extended permissions) */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <DateSwitcher 
          date={date} 
          setDate={setDate} 
          maxDate={(isTechnician && !hasExtendedPermissions) ? moment().tz('America/Los_Angeles').format() : null}
        />
      </Box>

      {/* Mobile View - Cards */}
      {isMobile ? (
        <Box>
          {appointments?.length ? (
            appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
              <MobileAppointmentCard key={appointment.id} appointment={appointment} />
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No appointments found</Typography>
            </Paper>
          )}
        </Box>
      ) : (
        /* Desktop View - Table */
        <TableContainer component={Paper} sx={{ ...table_container_base }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '20%' }}>Name</TableCell>
                <TableCell sx={{ width: '40%' }}>Address</TableCell>
                <TableCell sx={{ width: '15%' }}>Type</TableCell>
                <TableCell sx={{ width: '12.5%' }}>Check In</TableCell>
                <TableCell sx={{ width: '12.5%' }}>Check Out</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments?.length ? (
                appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
                  <TableRow key={appointment.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Link
                        href={`/appointments/${appointment.id}`}
                        color="rgba(0, 0, 0, 0.87)"
                        underline="none"
                        sx={link_styles}
                      >
                        {appointment.user?.fullName}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`/appointments/${appointment.id}`}
                        color="rgba(0, 0, 0, 0.87)"
                        underline="none"
                        sx={link_styles}
                      >
                        {appointment.property?.address}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`/appointments/${appointment.id}`}
                        color="rgba(0, 0, 0, 0.87)"
                        underline="none"
                        sx={link_styles}
                      >
                        {appointment.type?.name || '-'}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link
                          href={`/appointments/${appointment.id}`}
                          color="rgba(0, 0, 0, 0.87)"
                          underline="none"
                          sx={{ ...link_styles, flex: 1 }}
                        >
                          {appointment?.checkIn ? moment(appointment?.checkIn).tz('America/Los_Angeles').format('hh:mm A') : '-'}
                        </Link>
                        {isAdmin && appointment?.checkIn && (
                          <Tooltip title="Reset Check In">
                            <IconButton size="small" sx={{ p: 0.25 }} color="error"
                              onClick={() => handleResetTime(appointment.id, 'checkIn')}>
                              <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link
                          href={`/appointments/${appointment.id}`}
                          color="rgba(0, 0, 0, 0.87)"
                          underline="none"
                          sx={{ ...link_styles, flex: 1 }}
                        >
                          {appointment?.checkOut ? moment(appointment?.checkOut).tz('America/Los_Angeles').format('hh:mm A') : '-'}
                        </Link>
                        {isAdmin && appointment?.checkOut && (
                          <Tooltip title="Reset Check Out">
                            <IconButton size="small" sx={{ p: 0.25 }} color="error"
                              onClick={() => handleResetTime(appointment.id, 'checkOut')}>
                              <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                    <strong>No Data</strong>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50, 100]}
        component="div"
        count={appointments?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? '' : 'Rows per page'}
        sx={{
          '.MuiTablePagination-selectLabel': {
            display: { xs: 'none', md: 'block' },
          },
        }}
      />
    </Box>
  );
};

export default Timer;
